/**
 * Wait command - block until tasks reach terminal state
 *
 * Since sandbox exec blocks until done, tasks stored in local records
 * should already be in a terminal state. This function polls local task
 * records until all specified tasks are completed, failed, or cancelled.
 */

import type { WaitResult, TaskStatus } from '@spacecode/shared/remote';

const POLL_INTERVAL_MS = 5000;

/**
 * Wait for multiple tasks to reach terminal state.
 */
export async function waitForTasks(
  taskIds: string[],
  timeoutMinutes = 30,
): Promise<WaitResult> {
  if (taskIds.length === 0) {
    return { completed: 0, failed: 0, cancelled: 0, timedOut: false, tasks: [] };
  }

  const { getTask, updateTask } = await import('../config.js');

  const startTime = Date.now();
  const timeoutMs = timeoutMinutes * 60 * 1000;

  const terminalStatuses: Set<TaskStatus> = new Set(['completed', 'failed', 'cancelled']);

  while (true) {
    const elapsed = Date.now() - startTime;
    if (elapsed >= timeoutMs) {
      // Build final result with timeout flag
      return buildResult(taskIds, true, startTime);
    }

    // Check all tasks via local records
    let allDone = true;
    for (const taskId of taskIds) {
      const task = await getTask(taskId);
      if (!task) continue;

      if (terminalStatuses.has(task.status)) continue;

      // Task is still active in local records.
      // Sandbox tasks complete synchronously, so a "running" task likely
      // means another process is still executing it. Wait and re-check.
      allDone = false;
    }

    if (allDone) {
      return buildResult(taskIds, false, startTime);
    }

    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}

/**
 * Build the final WaitResult from current task states.
 */
async function buildResult(
  taskIds: string[],
  timedOut: boolean,
  startTime: number,
): Promise<WaitResult> {
  const { getTask } = await import('../config.js');

  let completed = 0;
  let failed = 0;
  let cancelled = 0;
  const taskResults: WaitResult['tasks'] = [];

  for (const taskId of taskIds) {
    const task = await getTask(taskId);
    if (!task) {
      taskResults.push({
        taskId,
        repo: 'unknown',
        status: 'failed',
        error: 'Task not found',
      });
      failed++;
      continue;
    }

    const durationSeconds = task.completedAt
      ? Math.round((new Date(task.completedAt).getTime() - new Date(task.createdAt).getTime()) / 1000)
      : Math.round((Date.now() - new Date(task.createdAt).getTime()) / 1000);

    taskResults.push({
      taskId: task.id,
      repo: task.repo,
      branch: task.branch,
      status: task.status,
      prUrl: task.prUrl,
      error: task.error,
      durationSeconds,
    });

    switch (task.status) {
      case 'completed': completed++; break;
      case 'failed': failed++; break;
      case 'cancelled': cancelled++; break;
    }
  }

  return { completed, failed, cancelled, timedOut, tasks: taskResults };
}
